// /family — caregiver / family mode UI. Primary users (Family plan)
// create a household and invite up to MAX members. Members see a
// read-only view of the primary user's medications + safety alerts.
//
// V1.1 scope: this page wires the existing households API. Full
// read-only dashboard for caregivers will land in V1.2.
//
// CLAUDE-RESEARCH-GAP: Full caregiver/proxy mode (#23 in
// qa/focus-group/FIX_LIST.md). Today the household model treats every
// user as the primary subject of their own data; caregivers see
// read-only views via household membership. The persona-focus-group
// asked for a true sub-profile model where the account holder is the
// caregiver and the patient is a managed sub-profile, with all
// caregiver-entered data tagged "Entered by Gloria". That requires a
// new users.parent_user_id (or households.subjects[] join table), an
// active-subject switcher in the global shell, audit-log tagging of
// caregiver-vs-self writes, and a first-run "who is this for?" prompt.
// Defer to V1.2.
// Suggested next research: review Apple Health "Add a Medical ID for
// someone else", MyChart "Manage another person's care", and Hero
// caregiver onboarding. Pick a permission model.
// Priority: high. Owner: Matt + Siggy.

import { useEffect, useState } from 'react';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useMe } from '../hooks/useMe';
import { AppPage } from '../layouts/AppPage';

interface HouseholdSummary {
  id: string;
  name: string;
  isPrimary: boolean;
  role: string;
  memberCount: number;
}

export function Family(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [households, setHouseholds] = useState<HouseholdSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    const res = await authedFetch('/api/households');
    const json = (await res.json()) as { ok: boolean; data?: HouseholdSummary[] };
    if (json.ok && Array.isArray(json.data)) setHouseholds(json.data);
    setLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only fetch
  useEffect(() => {
    void reload();
  }, []);

  const isFamilyTier = me?.tier === 'FAMILY';

  const createHousehold = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authedFetch('/api/households', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim() }),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message: string } };
      if (!json.ok) {
        setError(json.error?.message ?? 'Could not create household.');
        return;
      }
      setName('');
      setMessage('Household created.');
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async (householdId: string) => {
    if (!inviteEmail.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authedFetch(`/api/households/${householdId}/invite`, {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase() }),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message: string } };
      if (!json.ok) {
        setError(json.error?.message ?? 'Could not send invite.');
        return;
      }
      setMessage(`Invite sent to ${inviteEmail}.`);
      setInviteEmail('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppPage title="Family" back={{ to: '/account', label: 'Account' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Family care</p>
          <p className="food-intro__text">
            Add up to six loved ones so a trusted adult can see your medicine list and watch for
            safety alerts. They never need to log in to MedReview every day; they just get a heads
            up when something changes.
          </p>
        </div>
      </section>

      <section className="account-section" aria-labelledby="family-access-title">
        <h3 id="family-access-title" className="account-section__title">
          What your family sees
        </h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          You control this. Family members get a read-only view; they cannot edit or delete
          anything.
        </p>
        <div className="account-row">
          <span className="account-row__label">Your medicines and doses</span>
          <span className="account-row__value">Shared</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Safety alerts from the clinical engine</span>
          <span className="account-row__value">Shared</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Missed-dose summary (weekly)</span>
          <span className="account-row__value">Shared</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Your doctor directory + appointments</span>
          <span className="account-row__value">Shared</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Your private chats with Siggy</span>
          <span className="account-row__value">Private</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Your symptom check-ins</span>
          <span className="account-row__value">Private</span>
        </div>
        <div className="account-row">
          <span className="account-row__label">Your account, billing, login</span>
          <span className="account-row__value">Private</span>
        </div>
      </section>

      {!isFamilyTier ? (
        <section className="upsell-card">
          <h3 className="upsell-card__title">Your first family member is free, forever.</h3>
          <p className="upsell-card__body">
            To add up to 5 more loved ones, the Family plan is $14.99/mo (or $149/yr). You'll only
            see that price when you try to add a second invite.
          </p>
          <a href="/compare-plans" className="upsell-card__cta">
            Compare plans
          </a>
        </section>
      ) : null}

      {loading ? <p style={{ padding: 'var(--space-md)' }}>Loading...</p> : null}

      {message ? (
        <section className="account-section">
          <p style={{ color: 'var(--color-success)', margin: 0 }}>{message}</p>
        </section>
      ) : null}
      {error ? (
        <section className="account-section">
          <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p>
        </section>
      ) : null}

      {!loading && households.length === 0 ? (
        <section aria-hidden="true" style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <img
            src="/illustrations/empty/family-empty.png"
            alt=""
            loading="lazy"
            decoding="async"
            style={{
              width: 220,
              maxWidth: '60%',
              height: 'auto',
              margin: '0 auto',
              display: 'block',
            }}
          />
        </section>
      ) : null}

      {households.length === 0 ? (
        <section className="account-section">
          <h3 className="account-section__title">Create your household</h3>
          <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
            Give it a name your family will recognize. You can add members in the next step.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Our family"
              style={{
                flex: 1,
                padding: '12px 14px',
                fontSize: 18,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            />
            <SeniorButton
              variant="primary"
              onClick={createHousehold}
              disabled={busy || !name.trim()}
            >
              Create
            </SeniorButton>
          </div>
        </section>
      ) : null}

      {households.map((h) => (
        <section key={h.id} className="account-section">
          <h3 className="account-section__title">
            {h.name}
            <span
              className="status-pill"
              style={{
                marginLeft: 12,
                fontSize: 12,
                background: 'var(--color-accent-soft)',
                color: 'var(--color-accent)',
              }}
            >
              {h.isPrimary ? 'You manage this' : h.role}
            </span>
          </h3>
          <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
            {h.memberCount} member{h.memberCount === 1 ? '' : 's'} of up to 6.
          </p>
          {h.isPrimary ? (
            <div style={{ display: 'grid', gap: 8 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>Invite by email</span>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="loved-one@example.com"
                  style={{
                    padding: '12px 14px',
                    fontSize: 18,
                    border: '1.5px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                  }}
                />
              </label>
              {!isFamilyTier ? (
                <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>
                  First invite is free. Adding a second loved one needs the Family plan.
                </p>
              ) : null}
              <SeniorButton
                variant="primary"
                fullWidth
                onClick={() => void sendInvite(h.id)}
                disabled={busy || !inviteEmail.includes('@')}
              >
                Send invite
              </SeniorButton>
            </div>
          ) : (
            <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: 0 }}>
              You can see this household's medicines and safety alerts.
            </p>
          )}
        </section>
      ))}

      <section className="account-section">
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: 0 }}>
          Family members only see what is needed. Conversations with Siggy and personal symptom logs
          stay private to you.
        </p>
      </section>
    </AppPage>
  );
}
